import { Button, Check, Chevron, Remove } from '@lmc/ui-kit'
import { colours } from '@lmc/ui-kit/src/config/theme'
import { Text } from '@lmc/ui-kit/src/general'
import { showToast } from 'common/utils'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHasAiFunctionalityEnabled from 'hooks/useHasAiFunctionalityEnabled'
import {
  type NeedSuggestion,
  type SuggestionDecision,
  useNeedSuggestionRejectAll,
  useNeedSuggestions
} from 'hooks/useNeedSuggestions'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { keyframes } from 'styled-components'
import AiIcon from './AiIcon'
import RemoveSuggestionsModal from './RemoveSuggestionsModal'

/** The panel shows this many rows until "Show all suggestions" (per design). */
const COLLAPSED_ROW_COUNT = 3

/**
 * Accent colours for this surface, taken from the Figma prototype. The ui-kit
 * theme has no equivalents and the design tokens are being introduced
 * separately, so they stay local to the feature rather than in a new shared
 * module. Everything else here uses the ui-kit theme.
 */
const ACCENT = {
  panelBg: '#FBF7FF',
  betaChipBg: '#FFF7FA',
  betaChipBorder: '#FFA7D5',
  betaChipText: '#A3005A'
} as const

interface SuggestedNeedsPanelProps {
  residentId: string
  carePlanAdlId?: string
  /** For the explainer / success-card copy. */
  residentFirstName?: string
  /** The home's care-plans nominative for the success-card copy. */
  carePlanNominative?: string
  /**
   * Locally staged decisions, owned by the review form so saving the review
   * can commit them and Cancel/navigate-away can discard them.
   */
  decisions: SuggestionDecision[]
  onChangeDecisions: (next: SuggestionDecision[]) => void
}

/**
 * The "Suggested needs" strip at the bottom of the Needs card, per the Figma
 * prototype: an accordion of AI-suggested needs, one expanded at a time.
 * "Use this need" stages an add locally (the row flips to a "Need added"
 * card and a draft row joins the linked-needs list); staged adds are only
 * sent to the server when the review is saved, and Cancel discards them.
 * "Don't show this again" is the exception: it rejects every pending
 * suggestion server-side immediately (see `removeAllSuggestions`).
 */
const SuggestedNeedsPanel: React.FC<SuggestedNeedsPanelProps> = ({
  residentId,
  carePlanAdlId,
  residentFirstName,
  carePlanNominative,
  decisions,
  onChangeDecisions
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const headingId = useId()

  const { data: flags } = useFeatureFlags({
    flagNames: ['initial_assessments_needs_suggestions_review']
  })

  const aiFunctionalityEnabled = useHasAiFunctionalityEnabled()

  const suggestionsEnabled =
    (flags.initial_assessments_needs_suggestions_review?.enabled ?? false) &&
    aiFunctionalityEnabled

  const { suggestions, isLoading, isError, refetch } = useNeedSuggestions({
    residentId,
    carePlanAdlId,
    enabled: suggestionsEnabled
  })
  const { rejectAll, isRejecting } = useNeedSuggestionRejectAll(residentId)

  // One row expanded at a time (per design).
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [announcement, setAnnouncement] = useState('')

  // "Use this need" swaps the clicked button for the static success card —
  // park keyboard focus on the panel heading so it isn't dropped.
  const headingRef = useRef<HTMLHeadingElement>(null)
  const pendingHeadingFocus = useRef(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: focus runs after the decision-driven re-render
  useEffect(() => {
    if (!pendingHeadingFocus.current) return
    pendingHeadingFocus.current = false
    headingRef.current?.focus()
  }, [decisions])

  if (!suggestionsEnabled) return null

  const decisionFor = (suggestionId: string) =>
    decisions.find((d) => d.suggestion.id === suggestionId)

  // Render every fetched suggestion, plus any staged decision whose
  // suggestion has meanwhile left the pending list (e.g. a background
  // refetch after another user actioned it). Added suggestions stay as
  // "Need added" cards; dismissed ones (via "Don't show this again") hide.
  const orphanDecisions = decisions.filter(
    (d) => !suggestions.some((s) => s.id === d.suggestion.id)
  )
  const panelRows: NeedSuggestion[] = [
    ...suggestions,
    ...orphanDecisions.map((d) => d.suggestion)
  ].filter((s) => decisionFor(s.id)?.action !== 'dismiss')

  if (!isLoading && !isError && panelRows.length === 0) return null

  const visibleRows = showAll
    ? panelRows
    : panelRows.slice(0, COLLAPSED_ROW_COUNT)

  const stageSuggestion = (suggestion: NeedSuggestion) => {
    onChangeDecisions([
      ...decisions.filter((d) => d.suggestion.id !== suggestion.id),
      { suggestion, action: 'add', stagedOn: dayjs().toISOString() }
    ])
    setAnnouncement(
      t(carePlansConstants.SUGGESTED_NEED_ANNOUNCE_ADD, {
        need: suggestion.need,
        name: residentFirstName ?? ''
      })
    )
    if (expandedId === suggestion.id) setExpandedId(null)
    pendingHeadingFocus.current = true
  }

  // "Don't show this again" is permanent and immediate (NOT staged): the
  // design's acceptance criteria require the choice to survive reloads and
  // apply per resident, so it can't wait for the review save. Every pending
  // suggestion is rejected server-side; suggestions with a staged add are
  // left alone so their accept still commits on save. Newly generated
  // suggestions can still appear until the CM API soft delete lands.
  const removeAllSuggestions = async () => {
    const pending = panelRows.filter((s) => !decisionFor(s.id))
    try {
      await rejectAll(pending.map((s) => s.id))
      setShowRemoveModal(false)
      // A toast, not the panel's live region: removing the last pending
      // suggestion unmounts the panel, taking that region with it before
      // assistive tech can read it.
      showToast({ title: t(carePlansConstants.SUGGESTED_NEEDS_REMOVED_TOAST) })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
      showToast({
        type: 'error',
        title: t(carePlansConstants.REMOVE_SUGGESTIONS_ERROR)
      })
    }
  }

  return (
    <PanelWrapper
      as="section"
      aria-labelledby={headingId}
      data-testid="suggested-needs-panel"
    >
      <PanelHeader>
        <PanelHeading>
          <TitleRow>
            <IconTitle>
              <AiIcon />
              <PanelTitle id={headingId} ref={headingRef} tabIndex={-1}>
                {t(carePlansConstants.SUGGESTED_NEEDS_TITLE)}
              </PanelTitle>
            </IconTitle>
            <BetaChip>{t(carePlansConstants.SUGGESTED_NEEDS_AI_BETA)}</BetaChip>
          </TitleRow>
          <Explainer>
            {t(carePlansConstants.SUGGESTED_NEEDS_EXPLAINER, {
              name: residentFirstName ?? ''
            })}
          </Explainer>
        </PanelHeading>
        <DontShowButton
          type="button"
          variant="text"
          icon={<Remove />}
          onClick={() => setShowRemoveModal(true)}
        >
          {t(carePlansConstants.SUGGESTED_NEEDS_DONT_SHOW)}
        </DontShowButton>
      </PanelHeader>

      {isError ? (
        <ErrorRow>
          <Text>{t(carePlansConstants.SUGGESTED_NEEDS_LOAD_ERROR)}</Text>
          <Button type="button" variant="default" onClick={() => refetch()}>
            {t(carePlansConstants.SUGGESTED_NEEDS_RETRY)}
          </Button>
        </ErrorRow>
      ) : isLoading ? (
        <RowsList aria-hidden="true">
          <SkeletonRow />
          <SkeletonRow />
        </RowsList>
      ) : (
        <RowsList
          role="list"
          aria-label={t(carePlansConstants.SUGGESTED_NEEDS_LIST_ARIA)}
        >
          {visibleRows.map((suggestion) => {
            const isAdded = decisionFor(suggestion.id)?.action === 'add'
            const isExpanded = expandedId === suggestion.id && !isAdded
            const contentId = `${headingId}-note-${suggestion.id}`

            return (
              <SuggestionRow
                key={suggestion.id}
                role="listitem"
                data-testid="suggested-need-row"
              >
                {isAdded ? (
                  <>
                    <SuccessHeader data-testid="suggested-need-added">
                      <CheckIconBox>
                        <Check color={colours.status.success} />
                      </CheckIconBox>
                      <SuccessTitle>
                        {t(carePlansConstants.SUGGESTED_NEED_ADDED)}
                      </SuccessTitle>
                    </SuccessHeader>
                    <SuccessText>
                      {t(carePlansConstants.SUGGESTED_NEED_ADDED_TO, {
                        name: residentFirstName ?? '',
                        carePlans:
                          carePlanNominative ||
                          t(carePlansConstants.CARE_PLANS_NOMINATIVE_DEFAULT)
                      })}
                    </SuccessText>
                  </>
                ) : (
                  <>
                    <RowHeaderButton
                      type="button"
                      aria-expanded={isExpanded}
                      aria-controls={contentId}
                      aria-label={t(
                        carePlansConstants.SUGGESTED_NEED_REVIEW_ARIA,
                        { need: suggestion.need }
                      )}
                      onClick={() =>
                        setExpandedId(isExpanded ? null : suggestion.id)
                      }
                    >
                      <RowName>{suggestion.need}</RowName>
                      <Chevron direction={isExpanded ? 'up' : 'down'} />
                    </RowHeaderButton>
                    {isExpanded && (
                      <RowContent id={contentId}>
                        {suggestion.note && (
                          <NoteText>{suggestion.note}</NoteText>
                        )}
                        <div>
                          <Button
                            type="button"
                            variant="default"
                            aria-label={t(
                              carePlansConstants.SUGGESTED_NEED_USE_ARIA,
                              { need: suggestion.need }
                            )}
                            onClick={() => stageSuggestion(suggestion)}
                          >
                            {t(carePlansConstants.SUGGESTED_NEED_USE)}
                          </Button>
                        </div>
                      </RowContent>
                    )}
                  </>
                )}
              </SuggestionRow>
            )
          })}
        </RowsList>
      )}

      <PanelFooter>
        {panelRows.length > COLLAPSED_ROW_COUNT && (
          <ShowAllButton
            type="button"
            variant="text"
            aria-expanded={showAll}
            onClick={() => setShowAll((prev) => !prev)}
          >
            {t(
              showAll
                ? carePlansConstants.SUGGESTED_NEEDS_SHOW_LESS
                : carePlansConstants.SUGGESTED_NEEDS_SHOW_ALL
            )}
            <Chevron direction={showAll ? 'up' : 'down'} />
          </ShowAllButton>
        )}
        <Disclaimer>
          {t(carePlansConstants.SUGGESTED_NEEDS_DISCLAIMER)}
        </Disclaimer>
      </PanelFooter>

      <VisuallyHidden role="status">{announcement}</VisuallyHidden>

      <RemoveSuggestionsModal
        isModalVisible={showRemoveModal}
        setIsModalVisible={setShowRemoveModal}
        residentFirstName={residentFirstName}
        isRemoving={isRejecting}
        onConfirm={removeAllSuggestions}
        onCancel={() => setShowRemoveModal(false)}
      />
    </PanelWrapper>
  )
}

/* Purple strip flush with the bottom of the Needs card, per the Figma
   prototype (.suggest): panel bg purple-50, white rows with subtle borders.
   The -24px margins bleed through FormCard's 24px padding; the corner
   radius matches the card's 20px so the strip sits flush. */
const PanelWrapper = styled.div`
  /* Positioned so the visually-hidden live region's absolute positioning is
     contained here — without this it escapes the screen's scroll container
     and stretches the document with dead scroll space. */
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${ACCENT.panelBg};
  margin: 0 -24px -24px;
  padding: 12px 24px;
  border-radius: 0 0 20px 20px;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`

const PanelHeading = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const PanelTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};

  &:focus {
    outline: none;
  }
`

const BetaChip = styled.span`
  background: ${ACCENT.betaChipBg};
  border: 1px solid ${ACCENT.betaChipBorder};
  color: ${ACCENT.betaChipText};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-transform: uppercase;
`

/* Links inside the panel use the design's action-primary blue (the Figma
   tokens), not the app's legacy antd link blue. */
const PanelTextButton = styled(Button)`
  color: ${({ theme }) => theme.color.status.primary};

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.color.hover.primary};
  }
`

const Explainer = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const DontShowButton = styled(PanelTextButton)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }

  svg path {
    stroke: currentColor;
  }
`

const RowsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SuggestionRow = styled.div`
  background: ${({ theme }) => theme.color.neutral.color11};
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  border-radius: 8px;
  padding: 16px 12px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const RowHeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  text-align: left;

  /* 18px box gives the same visible glyph as the design's 24px chevron. */
  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
  }
`

const RowName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const RowContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const NoteText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color4};
  white-space: pre-wrap;
`

const SuccessHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const CheckIconBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  svg {
    width: 18px;
    height: 18px;
  }
`

const SuccessTitle = styled.span`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const SuccessText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color4};
  overflow: hidden;
  text-overflow: ellipsis;
`

const PanelFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const ShowAllButton = styled(PanelTextButton)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`

const Disclaimer = styled.span`
  flex: 1;
  min-width: 0;
  text-align: right;
  font-size: 14px;
  line-height: 22px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const shimmer = keyframes`
  0% { opacity: 0.45; }
  50% { opacity: 1; }
  100% { opacity: 0.45; }
`

const SkeletonRow = styled.div`
  height: 56px;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.neutral.color11};
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  animation: ${shimmer} 1.4s ease-in-out infinite;
`

const VisuallyHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`

export default SuggestedNeedsPanel
